import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Canonical search box for both apps — the integrator toolbar's `⌕ + input` field (and the inside
 * filter bars' PrimeNG `pi pi-search` inputs), unified into one control. A magnifier SVG + a
 * borderless text input inside an `h-9 rounded-lg` bordered box. Implements ControlValueAccessor so
 * it drops into BOTH `[(ngModel)]` (integrator) and `formControlName` (inside reactive forms).
 *
 * Width is the PARENT's concern (host is `display:block`, the inner box is `w-full`): size it via a
 * class on the element, e.g. `<wh-search-input class="sm:w-64" .../>`. Debouncing also stays in the
 * parent (its existing search subject / form debounce) — this control emits on every keystroke.
 *
 *   <wh-search-input [(ngModel)]="searchInput" (ngModelChange)="onSearchChange()" placeholder="…" />
 *   <wh-search-input formControlName="name" placeholder="Search by name…" />
 */
@Component({
  selector: 'wh-search-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => WhSearchInputComponent), multi: true }],
  template: `
    <div
      class="focus-within:border-primary-500 flex h-9 w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 transition-colors dark:border-slate-600 dark:bg-slate-800"
      [class.opacity-60]="disabled()">
      <svg
        class="size-4 shrink-0 text-gray-400 dark:text-slate-500"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        type="text"
        class="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 outline-none focus:ring-0 dark:text-slate-100"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onTouched()" />
    </div>
  `,
})
export class WhSearchInputComponent implements ControlValueAccessor {
  readonly placeholder = input<string>('Search…');

  protected readonly value = signal<string>('');
  protected readonly disabled = signal<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTouched: () => void = () => {};

  protected onInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.value.set(v);
    this.onChange(v);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
